import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Clock, Plus, CheckCircle, XCircle, Loader2, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Leave = () => {
    const { user } = useAuth();
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        start_date: '',
        end_date: '',
        reason: ''
    });

    useEffect(() => {
        fetchLeaveRequests();
    }, []);

    const fetchLeaveRequests = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/leave`, { withCredentials: true });
            setLeaveRequests(response.data);
        } catch (error) {
            toast.error('Failed to load leave requests');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await axios.post(`${API_URL}/api/leave`, formData, { withCredentials: true });
            toast.success('Leave request submitted');
            setDialogOpen(false);
            setFormData({ start_date: '', end_date: '', reason: '' });
            fetchLeaveRequests();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit request');
        } finally {
            setSaving(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'approved': return <CheckCircle className="h-4 w-4 text-[#10B981]" />;
            case 'rejected': return <XCircle className="h-4 w-4 text-[#EF4444]" />;
            default: return <Clock className="h-4 w-4 text-[#F59E0B]" />;
        }
    };

    const getStatusBadge = (status) => {
        const colors = {
            approved: 'bg-[#10B981]',
            rejected: 'bg-[#EF4444]',
            pending: 'bg-[#F59E0B]'
        };
        return <Badge className={colors[status] || colors.pending}>{status}</Badge>;
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="teacher-leave-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Leave Requests</h1>
                        <p className="text-[#4B5563]">Manage your leave applications</p>
                    </div>
                    <Button onClick={() => setDialogOpen(true)} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="request-leave-btn">
                        <Plus className="h-4 w-4 mr-2" /> Request Leave
                    </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {leaveRequests.map((request) => (
                        <Card key={request.id} className="card-surface">
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center">
                                        <Calendar className="h-5 w-5 mr-2 text-[#064E3B]" />
                                        <span className="font-semibold text-[#1F2937]">Leave Request</span>
                                    </div>
                                    {getStatusBadge(request.status)}
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-xs text-[#4B5563]">Duration</p>
                                        <p className="font-medium tabular-nums">
                                            {request.start_date} → {request.end_date}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-[#4B5563]">Reason</p>
                                        <p className="text-sm text-[#1F2937]">{request.reason}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-[#4B5563]">Submitted</p>
                                        <p className="text-sm tabular-nums">
                                            {new Date(request.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {leaveRequests.length === 0 && (
                    <Card className="card-surface">
                        <CardContent className="flex flex-col items-center justify-center py-12">
                            <Clock className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                            <p className="text-[#4B5563] mb-4">No leave requests yet</p>
                            <Button onClick={() => setDialogOpen(true)} className="bg-[#064E3B] hover:bg-[#047857]">
                                <Plus className="h-4 w-4 mr-2" /> Request Leave
                            </Button>
                        </CardContent>
                    </Card>
                )}

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit']">Request Leave</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Start Date</Label>
                                    <Input
                                        type="date"
                                        value={formData.start_date}
                                        onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>End Date</Label>
                                    <Input
                                        type="date"
                                        value={formData.end_date}
                                        onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Reason</Label>
                                <Textarea
                                    value={formData.reason}
                                    onChange={(e) => setFormData({...formData, reason: e.target.value})}
                                    placeholder="Please provide a reason for your leave request..."
                                    rows={4}
                                    required
                                />
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" disabled={saving} className="bg-[#064E3B] hover:bg-[#047857]">
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                    Submit Request
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default Leave;
